import { FAQ, FAQContentProps, FAQProps, SemaglutideFAQ, TirzepatideFAQ } from './faq.types';

const WhatIsTheDifferenceBetweenSemaglutideAndTirzepatideQuestion = {
  content: (
    <>
      Both semaglutide and tirzepatide are medications used to improve blood sugar control in adults
      with type 2 diabetes and to aid in weight loss for those who are obese or overweight.
      <br />
      <br />
      Tirzepatide works by activating both GLP-1 and GIP receptors, helping to enhance insulin
      release and promote weight loss. Semaglutide targets the GLP-1 receptor, which stimulates
      insulin and inhibits glucagon release — also aiding in appetite control. Both medications are
      administered through subcutaneous injections using a pen-like device, typically once a week.
      They also share common side effects.
      <br />
      <br />
      The dosing and titration schedules do differ between the medications. Your provider will
      ensure you are prescribed the appropriate dose based on your current prescribed dosage of
      tirzepatide.
    </>
  ),
  title: 'What is the difference between semaglutide and tirzepatide?'
};

const IsItSafeToSwitchFromTirzepatideToSemaglutide = {
  content:
    'Yes, switching from tirzepatide to semaglutide is safe, but it should always be done under the guidance of a licensed healthcare provider.',
  title: 'Is it safe to switch from tirzepatide to semaglutide?'
};

const WhatIsCustomCompoundingQuestion = {
  content:
    'Custom compounding involves creating medications by altering, mixing, or combining ingredients to tailor them to the specific needs of an individual patient. These compounded medications offer a level of personalization that standard prescription drugs do not provide. Patients should only use compounded medications when prescribed by a licensed health care provider. Compounding pharmacies must use pharmaceutical-grade ingredients sourced from FDA-registered facilities when creating compounded medications.',
  title: 'What is custom compounding?'
};

const WhatIngredientsAreInTheCustomTirzepatideCompoundQuestion = {
  content:
    'Your provider will be the best source for details on the specific formulation prescribed to you and can walk you through your questions.',
  title: 'What ingredients are in the custom Tirzepatide compound?'
};

const WillThereBeAPriceChangeForTheNewCompoundedOptionQuestion = {
  content: 'No, the cost of compounded tirzepatide will remain the same at this time.',
  title: 'Will there be a price change for the new compounded option?'
};

const getSemaglutideContent = ({
  screen,
  planType
}: Omit<FAQ & SemaglutideFAQ, 'medication' | 'isOpen' | 'onClose'>): FAQContentProps[] => {
  const isWegovyOffer = screen === 'wegovy-offer';
  const isQualifiedForWegovy = screen === 'qualified-for-wegovy';
  const isQualifiedForSemaglutide = screen === 'qualified-for-sema-plus';
  const isNotQualifiedForSemaglutide = screen === 'not-qualified-for-sema-plus';

  const isCareOnly = planType === 'care-only';
  const isBundle = planType === 'bundle';
  const isMaintenance = planType === 'maintenance';

  const isQualifiedForSemaglutideCareOnly = isQualifiedForSemaglutide && isCareOnly;
  const isQualifiedForSemaglutideBundle = isQualifiedForSemaglutide && isBundle;
  const isQualifiedForSemaglutideMaintenance = isQualifiedForSemaglutide && isMaintenance;

  const showWhatIsWegovyCashPay =
    isWegovyOffer || isQualifiedForWegovy || isNotQualifiedForSemaglutide;
  const showWhatIsCustomCompounding = isQualifiedForSemaglutide;
  const showDifferenceSubscriptionCost =
    isQualifiedForSemaglutideBundle || isQualifiedForSemaglutideMaintenance;

  return [
    ...(showWhatIsWegovyCashPay
      ? [
          {
            content: (
              <>
                The Wegovy® cash pay option allows eligible patients to purchase Wegovy®
                (semaglutide) directly through NovoCare® without using insurance, pending a valid
                prescription (e.g., from a LifeMD-affiliated provider). NovoCare® is a
                direct-to-patient pharmacy platform affiliated with Novo Nordisk, a global
                healthcare company and the manufacturer of Wegovy® (semaglutide). This cash pay
                option is available to patients who are uninsured or whose commercial insurance does
                not cover Wegovy®. To learn more about the Wegovy® cash pay  option (including how
                the process works), please review{' '}
                <a
                  className="cursor-pointer text-gray"
                  target="_blank"
                  href="https://lifemd.com/portal/content-new/how-to-purchase-wegovy-through-novocare-cash-pay"
                  rel="noreferrer"
                >
                  this article.
                </a>
              </>
            ),
            title: 'What is the Wegovy® cash pay option?'
          }
        ]
      : []),
    ...(isWegovyOffer
      ? [
          {
            content:
              'Custom compounding involves creating medications by altering, mixing, or combining ingredients to tailor them to the specific needs of an individual patient. These compounded medications offer a level of personalization that standard prescription drugs do not provide. Patients should only use compounded medications when prescribed by a licensed health care provider. Compounding pharmacies are required to use pharmaceutical-grade ingredients sourced from FDA-registered facilities when creating compounded medications. If applicable, your provider can guide you through the details of your prescribed formulation.',
            title: 'What is a customized Semaglutide option?'
          }
        ]
      : []),
    ...(isQualifiedForWegovy || isNotQualifiedForSemaglutide
      ? [
          {
            content: (
              <>
                Your experience will depend on your current plan:
                <br />
                <ul className="ml-5 list-outside list-disc">
                  <li>
                    If you’re on a bundle plan (care + medication), you’ll move to a LifeMD Weight
                    Management care-only subscription (i.e., covering the cost of your care).
                    NovoCare® Pharmacy will handle both the billing and fulfillment of your
                    medication.
                  </li>
                  <li>
                    If you’re already on a care-only plan, there will be no change to your
                    subscription. NovoCare® Pharmacy will simply take over the billing and
                    fulfillment of your medication.
                  </li>
                </ul>
              </>
            ),
            title:
              'How will my experience be different if I switch to the Wegovy® cash pay option?'
          }
        ]
      : []),
    ...(showWhatIsCustomCompounding ? [WhatIsCustomCompoundingQuestion] : []),
    ...(isQualifiedForSemaglutide
      ? [
          {
            content: (
              <>
                LifeMD works with multiple pharmacies to offer formulations of semaglutide to
                patients who have a clinically significant reason to receive a compounded version.
                These may include:
                <ul className="ml-5 list-outside list-disc">
                  <li>
                    Compounded semaglutide with glycine: Glycine is an amino acid, a natural
                    building block of proteins in the body. While the body produces glycine on its
                    own, it is also found in foods like meat, fish, dairy, and legumes. Glycine may
                    offer additional benefits, including support for blood pressure, blood sugar,
                    gut health, muscle recovery, stress, mood, and sleep.
                  </li>
                  <li>
                    Compounded semaglutide with niacinamide: Niacinamide, a form of vitamin B3, is
                    found in foods like meat, fish, milk, eggs, green vegetables, and cereals. It
                    plays a key role in fat and sugar metabolism and helps maintain healthy cells.
                    It may help support blood sugar control, skin health, energy levels, and joint
                    function.
                  </li>
                  <li>
                    Compounded semaglutide with glycine and B12: In addition to the benefits of
                    glycine described above, B12 is an important vitamin needed for a healthy
                    nervous system, the formation of red blood cells, and for building DNA. It is
                    also helpful to the breakdown of fats and proteins, contributing to a healthy
                    metabolism. Supplementation may be especially helpful for vegan/vegetarians and
                    those over the age of 50.
                  </li>
                </ul>
                If you have questions about the specific ingredients in your custom semaglutide
                compound, your provider can guide you through the details of your prescribed
                formulation.
              </>
            ),
            title: 'What ingredients are in the custom Semaglutide compound?'
          }
        ]
      : []),
    ...(showDifferenceSubscriptionCost
      ? [
          {
            content: isQualifiedForSemaglutideBundle
              ? 'Yes, medication costs will be separate and not bundled with the cost of care. The cost of care will be a set amount, and the medication cost will depend on the prescribed dose and how it is adjusted over time, at the recommendation of your provider. However, rest assured that the cost of the medication will remain at a reasonable $13–57 per week.'
              : 'No, there will be no cost difference.',
            title: 'Will there be a cost difference in what I’m paying for my subscription?'
          }
        ]
      : []),
    ...(isQualifiedForSemaglutideCareOnly
      ? [
          {
            content: 'No, there will be no cost difference.',
            title:
              'Is there a cost difference between the semaglutide medication I’m currently on and the custom semaglutide compound?'
          }
        ]
      : []),
    ...(isQualifiedForSemaglutide
      ? [
          {
            content:
              'Your experience will only be different if there is a change in the pharmacy that processes your prescription. In some cases, the pharmacy may collect payment directly — but rest assured the cost to you will remain the same.',
            title: 'Will my experience be different if I switch to a custom semaglutide compound?'
          }
        ]
      : []),
    ...(isNotQualifiedForSemaglutide
      ? [
          {
            content:
              'Triple Therapy is a treatment plan that combines three oral medications: metformin, bupropion, and topiramate. Each medication offers unique benefits for a comprehensive approach to healthy weight management.',
            title: 'What is Triple Therapy?'
          },
          {
            content:
              'If you switch to Triple Therapy, you’ll take a daily combination of three oral medications — metformin, bupropion, and topiramate — to support weight loss. For $129 a month, your subscription will include everything: medication, required lab work at Quest Diagnostics or Labcorp, shipping, and ongoing support from the LifeMD care team.',
            title: 'How will my experience be different if I switch to the Triple Therapy option?'
          }
        ]
      : [])
  ];
};

const getTirzepatideContent = (
  props: Omit<FAQ & TirzepatideFAQ, 'medication' | 'isOpen' | 'onClose'>
): FAQContentProps[] => {
  return [
    ...(props.type === 'eligible'
      ? [
          WhatIsCustomCompoundingQuestion,
          WhatIngredientsAreInTheCustomTirzepatideCompoundQuestion,
          WillThereBeAPriceChangeForTheNewCompoundedOptionQuestion
        ]
      : [
          {
            content:
              'On December 19, 2024 the U.S. Food and Drug Administration announced that the shortage of tirzepatide injection, a glucagon-like peptide 1 (GLP-1) medication, has been resolved. Tirzepatide is the active ingredient in the weight-loss medication Zepbound® and the diabetes medication Mounjaro®. This announcement also confirmed timelines for when pharmacies can no longer supply the essential copy of tirzepatide.',
            title: 'What did the FDA announce?'
          },
          WhatIsTheDifferenceBetweenSemaglutideAndTirzepatideQuestion,
          IsItSafeToSwitchFromTirzepatideToSemaglutide,
          ...(props.type === 'not-eligible-care-only'
            ? [
                {
                  content:
                    'As you are currently on a subscription plan that does not include the cost of medication, there will be no difference in subscription cost as a result of this change. While costs can vary based on dosage and titration, compounded semaglutide typically has a lower “out of pocket” cost compared to compounded tirzepatide. Therefore, your overall out-of-pocket cost is expected to decrease going forward.',
                  title: 'Is there a cost difference?'
                }
              ]
            : [])
        ])
  ];
};

export const getContent = (
  medication: FAQProps['medication'],
  props: Omit<FAQProps, 'medication' | 'isOpen' | 'onClose'>
) => {
  switch (medication) {
    case 'semaglutide':
      return getSemaglutideContent(
        props as Omit<FAQ & SemaglutideFAQ, 'medication' | 'isOpen' | 'onClose'>
      );
    case 'tirzepatide':
      return getTirzepatideContent(props);
    default:
      return [];
  }
};
